import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { capitalize, cn, toApiSort } from "@/lib/utils";
import { setUserData, setUserDataAction } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useDeletePropertyOwnershipMutation,
  useGetPropertyAssociationList
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, MoreVertical, Trash2 } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

const useUsers = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState([{ id: "ownershipId", desc: true }]);
  const [openDialog, setOpenDialog] = useState(false);
  const selectedRow = useRef<any>(null);
  const navigate = useNavigate();
  const { id, propertyId } = useParams();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const propertyDetails = useSelector(
    (state: RootState) => state.currentData.propertyData
  );

  const {
    data: usersGridData,
    isLoading: isUserGridLoading,
    refetch: refetchUserGridData,
    isRefetching: isRefetchingUserData
  } = useGetPropertyAssociationList({
    data: {
      filters: [
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "propertyId",
          value: propertyId,
          operator: "EQUAL",
        },

      ],
      pageSize,
      pageNumber,
      sort: toApiSort(sorting, { field: "ownershipId", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!(communityDetail?.communityId)
    }
  });
    
  useEffect(() => {
    refetchUserGridData();
  }, [pageNumber, pageSize, sorting]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.communities}`,
        name: `Community - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.communities}/${id}/${routes.properties}`,
        name: `Property - ${propertyDetails?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Manage Users"}</span>,
      },
    ],
    [propertyDetails]
  );

  const { mutate: deleteUser, isPending: isDeletePending } =
    useDeletePropertyOwnershipMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["propertyOccupancyList", propertyId],
        });
        navigate(
          `/${routes.cam}/${routes.communities}/${id}/${routes.properties}/${propertyId}/${routes.users}`
        );
        toast.success("User Deleted Successfully");
      },
    });

  const columns = [
    {
      accessorKey: "ownershipId",
      header: "Ownership ID",
    },
    {
      accessorKey: "userName",
      header: "User ID",
    },
    {
      accessorKey: "firstName",
      header: "First Name",
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
    },
    {
      accessorKey: "associationType",
      header: "Occupant Type",
      accessorFn: (row: any) => capitalize(row?.associationType) 
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: ({row}: any) => {
        return row?.original?.startDate
          ? moment(row?.original?.startDate).format("MM/DD/YYYY")
          : "";
      },
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: (row: any) => {
        return row?.original?.endDate
          ? moment(row.original.endDate).format("MM/DD/YYYY")
          : "";
      },
    },
    {
      accessorKey: "status",
      header: (
        <div className="flex items-center w-[80px] justify-center gap-2">
          Status
        </div>
      ),
      cell: ({row}: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.original?.endDate
                ? "border-red-600 bg-red-100 text-red-600"
                : "border-green-600 bg-green-100 text-green-600",
              "rounded-full px-4 py-2"
            )}
          >
            {row.original?.endDate ? "Inactive" : "Active"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "phoneNumber",
      header: "Mobile No.",
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({row}: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            {row.original.endDate ? (
              <></>
            ) : (
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setUserData(row.original));
                  dispatch(setUserDataAction("edit"));
                  navigate(`${row.original.ownershipId}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setOpenDialog(true);
                selectedRow.current = row.original;
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onAddUser = () => {
    dispatch(setUserDataAction(""));
    navigate(`${routes.add}`);
  };

  const onClose = () => {
    setOpenDialog(false);
  };

  const onConfirm = (reason: string) => {
    deleteUser({ id: selectedRow.current?.ownershipId, reason });
    onClose();
  };

  const onRowClick = (row: any) => {
    dispatch(setUserData(row.original));
    dispatch(setUserDataAction("view"));
    navigate(`${row.original.ownershipId}/view`);
  };

  return {
    globalFilter,
    setGlobalFilter,
    columns,
    onAddUser,
    breadcrumbData,
    propertyDetails,
    openDialog,
    onClose,
    onConfirm,
    isDeletePending,
    selectedRow: selectedRow.current,
    onRowClick,
    isLoading: isRefetchingUserData || isUserGridLoading,
    usersGridData,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    setPageNumber,
    setPageSize
  };
};

export default useUsers;
